import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { contactApiClient, ContactCreateErrorResponse } from '@src/api/company/contact/contactApiClient';
import { contactFormApiClient } from '@src/api/company/contact/contactFormApiClient';
import { CONTACT_TYPE, ContactType } from '@src/api/types/contact';
import { SupportServiceCategoryWithServices } from '@src/api/types/supportService';
import { useScrollToError } from '@src/hooks/useScrollToError';
import CONTACT_STORAGE_KEY, { CONTACT_INFLOW_ROUTE } from '@src/modules/constants/contact';
import { COMPANY_URL } from '@src/routes/company';
import { ErrorMessage } from '@src/ui/elements/form';
import Checkbox from '@src/ui/elements/form/Checkbox';
import Input from '@src/ui/elements/form/Input';
import Tag from '@src/ui/elements/form/Tag';
import Textarea from '@src/ui/elements/form/Textarea';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ContactConfirmModal from './ContactConfirmModal';

type ContactTypeOption = {
  type: ContactType;
  label: string;
  description: string;
};

const CONTACT_TYPES: ContactTypeOption[] = [
  {
    type: CONTACT_TYPE.GENERAL,
    label: import.meta.env.VITE_APP_PORTAL_NAME || 'プラットフォーム',
    description: 'プラットフォーム全体について',
  },
  {
    type: CONTACT_TYPE.SUPPORT_SERVICE,
    label: '支援サービス',
    description: '経営支援サービスに関するご相談',
  },
  {
    type: CONTACT_TYPE.OTHER,
    label: 'その他',
    description: '上記以外のお問い合わせ',
  },
];

type ContactFormValues = {
  contactTypeId: string;
  supportServiceIds: string[];
  companyName: string;
  companyNameKana: string;
  department: string;
  position: string;
  name: string;
  nameKana: string;
  email: string;
  emailConfirm: string;
  phoneNumber: string;
  contactContent: string;
};

type ContactFormErrors = ContactCreateErrorResponse['contact'];

const initialFormValues: ContactFormValues = {
  contactTypeId: '',
  supportServiceIds: [],
  companyName: '',
  companyNameKana: '',
  department: '',
  position: '',
  name: '',
  nameKana: '',
  email: '',
  emailConfirm: '',
  phoneNumber: '',
  contactContent: '',
};

function RequiredBadge() {
  return (
    <span className='mb-1 inline-flex h-4 w-7 items-center justify-center self-end bg-warning-500 text-[10px] leading-none text-white'>
      必須
    </span>
  );
}

function OptionalBadge() {
  return (
    <span className='mb-1 inline-flex h-4 w-7 items-center justify-center self-end bg-gray-200 text-[10px] leading-none text-gray-500'>
      任意
    </span>
  );
}

function AutoFilledHint() {
  return <span className='text-xs text-gray-450'>会員情報から自動入力</span>;
}

export default function ContactForm() {
  const [formValues, setFormValues] = useState<ContactFormValues>(initialFormValues);
  const [errors, setErrors] = useState<ContactFormErrors>();
  const [agreedToPrivacyPolicy, setAgreedToPrivacyPolicy] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [supportServiceCategories, setSupportServiceCategories] = useState<
    SupportServiceCategoryWithServices[]
  >([]);
  const navigate = useNavigate();
  const fieldRefs = useScrollToError(errors ?? {});
  const supportServiceItemRefs = useRef<Record<string, HTMLLabelElement | null>>({});
  const pendingScrollServiceIdRef = useRef<string | null>(null);
  const inflowRouteRef = useRef<number>(CONTACT_INFLOW_ROUTE.DIRECT_ACCESS);
  const inflowSupportServiceIdRef = useRef<number>();
  const contactIdRef = useRef<number>();
  const hasFetchedInitialDataRef = useRef(false);

  useEffect(() => {
    const pendingSupportServiceId = localStorage.getItem(CONTACT_STORAGE_KEY.SUPPORT_SERVICE_ID);
    if (pendingSupportServiceId) {
      pendingScrollServiceIdRef.current = pendingSupportServiceId;
      inflowRouteRef.current = CONTACT_INFLOW_ROUTE.SUPPORT_SERVICE;
      inflowSupportServiceIdRef.current = Number(pendingSupportServiceId);
      setFormValues((prev) => ({
        ...prev,
        contactTypeId: CONTACT_TYPE.SUPPORT_SERVICE,
        supportServiceIds: [pendingSupportServiceId],
      }));
      return;
    }

    const pendingInflowRoute = localStorage.getItem(CONTACT_STORAGE_KEY.INFLOW_ROUTE);
    if (pendingInflowRoute) {
      inflowRouteRef.current = Number(pendingInflowRoute);
    }
  }, []);

  useEffect(() => {
    const targetServiceId = pendingScrollServiceIdRef.current;
    if (!targetServiceId) {
      return;
    }
    const targetElement = supportServiceItemRefs.current[targetServiceId];
    if (!targetElement) {
      return;
    }
    targetElement.scrollIntoView({ block: 'nearest' });
    pendingScrollServiceIdRef.current = null;
  }, [supportServiceCategories]);

  useEffect(() => {
    if (hasFetchedInitialDataRef.current) {
      return;
    }
    hasFetchedInitialDataRef.current = true;

    contactFormApiClient
      .show({
        inflowRoute: inflowRouteRef.current,
        supportServiceId: inflowSupportServiceIdRef.current,
      })
      .then((response) => {
        handleApiResponse(response, (data) => {
          contactIdRef.current = data.contact_id;
          localStorage.removeItem(CONTACT_STORAGE_KEY.SUPPORT_SERVICE_ID);
          localStorage.removeItem(CONTACT_STORAGE_KEY.INFLOW_ROUTE);
          setSupportServiceCategories(
            Array.isArray(data.support_service_categories) ? data.support_service_categories : [],
          );

          if (!data.defaults) {
            return;
          }

          const defaults = data.defaults;
          setFormValues((prev) => ({
            ...prev,
            companyName: prev.companyName || defaults.company_name,
            companyNameKana: prev.companyNameKana || defaults.company_name_kana,
            department: prev.department || defaults.department,
            position: prev.position || defaults.position,
            name: prev.name || defaults.name,
            nameKana: prev.nameKana || defaults.name_kana,
            email: prev.email || defaults.email,
            emailConfirm: prev.emailConfirm || defaults.email,
          }));
        });
      });
  }, []);

  const handleOpenConfirmModal = () => {
    setIsConfirmModalOpen(true);
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current || contactIdRef.current === undefined) {
      return;
    }
    const contactId = contactIdRef.current;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const res = await contactApiClient.create({
        contact: {
          id: contactId,
          contact_category_code: formValues.contactTypeId,
          support_service_ids: formValues.supportServiceIds.map(Number),
          company_name: formValues.companyName,
          company_name_kana: formValues.companyNameKana,
          department: formValues.department,
          position: formValues.position,
          name: formValues.name,
          name_kana: formValues.nameKana,
          email: formValues.email,
          email_confirmation: formValues.emailConfirm,
          phone: formValues.phoneNumber,
          message: formValues.contactContent,
          privacy_policy_agreed: agreedToPrivacyPolicy,
        },
      });

      handleApiResponse(
        res,
        () => {
          navigate(COMPANY_URL.CONTACT_COMPLETE);
        },
        (validationErrors) => {
          setErrors(validationErrors.contact);
          setIsConfirmModalOpen(false);
        },
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  const selectedSupportServices = supportServiceCategories
    .flatMap((category) => category.support_services)
    .filter((service) => formValues.supportServiceIds.includes(String(service.id)));

  const handleContactTypeChange = (contactTypeId: string) => {
    setFormValues({ ...formValues, contactTypeId, supportServiceIds: [] });
  };

  const handleToggleSupportService = (supportServiceId: string) => {
    setFormValues((prev) => {
      const isSelected = prev.supportServiceIds.includes(supportServiceId);
      return {
        ...prev,
        supportServiceIds: isSelected
          ? prev.supportServiceIds.filter((id) => id !== supportServiceId)
          : [...prev.supportServiceIds, supportServiceId],
      };
    });
  };

  return (
    <main className='bg-gray-50 px-6 py-[60px] lg:px-10 sm:bg-transparent sm:px-5'>
      <div className='mx-auto max-w-[800px]'>
        <h1 className='text-center text-2xl font-semibold'>お問い合わせ</h1>
        <p className='mt-4 text-center text-sm text-gray-450'>
          支援サービス・プラットフォーム全体に関するご質問・ご相談を承ります。
          <br />
          内容を確認のうえ、担当者よりご連絡いたします。
        </p>

        <form
          className='mt-10 flex flex-col gap-[26px] rounded-lg border border-solid border-gray-200 bg-white p-10 lg:p-6 sm:border-0 sm:p-0'
          onSubmit={(e) => e.preventDefault()}
        >
          <div
            className='flex flex-col gap-3'
            ref={(el) => (fieldRefs.current['contact_category_code'] = el)}
          >
            <div className='flex items-center gap-3'>
              <span
                id='contact-type-label'
                className='font-medium'
              >
                問い合わせ内容
              </span>
              <RequiredBadge />
            </div>
            <div
              role='radiogroup'
              aria-labelledby='contact-type-label'
              className='flex flex-col gap-3'
            >
              {CONTACT_TYPES.map((contactType) => (
                <label
                  key={contactType.type}
                  className='block cursor-pointer pl-6 -indent-6'
                >
                  <input
                    type='radio'
                    name='contactTypeId'
                    data-testid={`contact-type-${contactType.type}`}
                    checked={formValues.contactTypeId === contactType.type}
                    onChange={() => handleContactTypeChange(contactType.type)}
                    className='mr-2 align-middle'
                  />
                  <span className='font-medium'>{contactType.label}</span>
                  <span className='[word-spacing:12px]'> </span>
                  <span className='whitespace-nowrap text-xs text-gray-450'>
                    － {contactType.description}
                  </span>
                </label>
              ))}
            </div>
            <ErrorMessage>{firstError(errors?.contact_category_code)}</ErrorMessage>
          </div>

          {formValues.contactTypeId === CONTACT_TYPE.SUPPORT_SERVICE && (
            <div
              className='flex flex-col gap-3'
              ref={(el) => (fieldRefs.current['support_service_ids'] = el)}
            >
              <div className='flex flex-wrap items-center gap-3'>
                <span
                  id='support-service-label'
                  className='font-medium'
                >
                  ご希望の支援サービス
                </span>
                <RequiredBadge />
                <span className='text-xs text-gray-450'>複数選択できます</span>
              </div>

              <div
                role='group'
                aria-labelledby='support-service-label'
                className={`max-h-64 overflow-y-auto rounded-md border border-solid bg-white ${
                  hasError(errors?.support_service_ids) ? 'border-warning-500' : 'border-gray-200'
                }`}
              >
                {supportServiceCategories.map((category) => (
                  <div key={category.id}>
                    <p className='bg-gray-50 px-4 py-2 text-xs font-medium text-gray-450'>
                      {category.name}
                    </p>
                    {category.support_services.map((service) => {
                      const isSelected = formValues.supportServiceIds.includes(String(service.id));
                      return (
                        <label
                          key={service.id}
                          ref={(el) => (supportServiceItemRefs.current[String(service.id)] = el)}
                          className={`flex cursor-pointer items-center gap-2 border-t border-solid border-gray-100 px-4 py-3 ${
                            isSelected ? 'bg-primary-50' : ''
                          }`}
                        >
                          <input
                            type='checkbox'
                            data-testid={`support-service-${service.id}`}
                            checked={isSelected}
                            onChange={() => handleToggleSupportService(String(service.id))}
                            className='shrink-0 self-center'
                          />
                          <span>{service.title}</span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>

              {selectedSupportServices.length > 0 && (
                <div
                  className='flex flex-col gap-2'
                  data-testid='selected-support-services'
                >
                  <span className='text-sm text-primary-500'>
                    選択中：{selectedSupportServices.length}件
                  </span>
                  <div className='flex flex-wrap items-center gap-2'>
                    {selectedSupportServices.map((service, index) => (
                      <Tag
                        key={service.id}
                        variant='primary-light'
                        index={index}
                        text={service.title}
                        onClick={() => handleToggleSupportService(String(service.id))}
                      />
                    ))}
                  </div>
                </div>
              )}

              <ErrorMessage>{firstError(errors?.support_service_ids)}</ErrorMessage>
            </div>
          )}

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['company_name'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-company-name'
                  className='font-medium'
                >
                  企業名
                </label>
                <RequiredBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-company-name'
              value={formValues.companyName}
              onChange={(e) => setFormValues({ ...formValues, companyName: e.target.value })}
              error={hasError(errors?.company_name)}
              placeholder='いろはにほへと商事株式会社'
            />
            <ErrorMessage>{firstError(errors?.company_name)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['company_name_kana'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-company-name-kana'
                  className='font-medium'
                >
                  企業名（フリガナ）
                </label>
                <RequiredBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-company-name-kana'
              value={formValues.companyNameKana}
              onChange={(e) => setFormValues({ ...formValues, companyNameKana: e.target.value })}
              error={hasError(errors?.company_name_kana)}
              placeholder='イロハニホヘトショウジカブシキガイシャ'
            />
            <ErrorMessage>{firstError(errors?.company_name_kana)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['department'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-department'
                  className='font-medium'
                >
                  部署名
                </label>
                <OptionalBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-department'
              value={formValues.department}
              onChange={(e) => setFormValues({ ...formValues, department: e.target.value })}
              error={hasError(errors?.department)}
              placeholder='経営企画部'
            />
            <ErrorMessage>{firstError(errors?.department)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['position'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-position'
                  className='font-medium'
                >
                  役職
                </label>
                <OptionalBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-position'
              value={formValues.position}
              onChange={(e) => setFormValues({ ...formValues, position: e.target.value })}
              error={hasError(errors?.position)}
              placeholder='部長'
            />
            <ErrorMessage>{firstError(errors?.position)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['name'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-name'
                  className='font-medium'
                >
                  氏名
                </label>
                <RequiredBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-name'
              value={formValues.name}
              onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
              error={hasError(errors?.name)}
              placeholder='山田 太郎'
            />
            <ErrorMessage>{firstError(errors?.name)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['name_kana'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-name-kana'
                  className='font-medium'
                >
                  氏名（フリガナ）
                </label>
                <RequiredBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-name-kana'
              value={formValues.nameKana}
              onChange={(e) => setFormValues({ ...formValues, nameKana: e.target.value })}
              error={hasError(errors?.name_kana)}
              placeholder='ヤマダ タロウ'
            />
            <ErrorMessage>{firstError(errors?.name_kana)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['email'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-email'
                  className='font-medium'
                >
                  メールアドレス
                </label>
                <RequiredBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-email'
              type='email'
              value={formValues.email}
              onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
              error={hasError(errors?.email)}
              placeholder='sample@sample.co.jp'
            />
            <ErrorMessage>{firstError(errors?.email)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['email_confirmation'] = el)}
          >
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-3'>
                <label
                  htmlFor='contact-email-confirm'
                  className='font-medium'
                >
                  メールアドレス（確認）
                </label>
                <RequiredBadge />
              </div>
              <AutoFilledHint />
            </div>
            <Input
              id='contact-email-confirm'
              type='email'
              value={formValues.emailConfirm}
              onChange={(e) => setFormValues({ ...formValues, emailConfirm: e.target.value })}
              error={hasError(errors?.email_confirmation)}
              placeholder='sample@sample.co.jp'
            />
            <ErrorMessage>{firstError(errors?.email_confirmation)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['phone'] = el)}
          >
            <div className='flex items-center gap-3'>
              <label
                htmlFor='contact-phone-number'
                className='font-medium'
              >
                電話番号（ハイフンなし、半角数字）
              </label>
              <OptionalBadge />
            </div>
            <Input
              id='contact-phone-number'
              type='tel'
              value={formValues.phoneNumber}
              onChange={(e) => setFormValues({ ...formValues, phoneNumber: e.target.value })}
              error={hasError(errors?.phone)}
              placeholder='0312345678'
            />
            <ErrorMessage>{firstError(errors?.phone)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['message'] = el)}
          >
            <div className='flex items-center gap-3'>
              <label
                htmlFor='contact-content'
                className='font-medium'
              >
                お問い合わせ内容
              </label>
              <RequiredBadge />
            </div>
            <Textarea
              id='contact-content'
              value={formValues.contactContent}
              onChange={(e) => setFormValues({ ...formValues, contactContent: e.target.value })}
              height='160px'
              error={hasError(errors?.message)}
              placeholder='ご質問・ご相談の内容をご記入ください。'
            />
            <ErrorMessage>{firstError(errors?.message)}</ErrorMessage>
          </div>

          <div
            className='flex flex-col gap-[6px]'
            ref={(el) => (fieldRefs.current['privacy_policy_agreed'] = el)}
          >
            <div className='flex items-center justify-center gap-2 rounded-md bg-gray-50 p-4'>
              <Checkbox
                id='privacy-policy-checkbox'
                checked={agreedToPrivacyPolicy}
                onChange={(e) => setAgreedToPrivacyPolicy(e.target.checked)}
                className='mt-0.5 shrink-0 self-center leading-none'
              />
              <label htmlFor='privacy-policy-checkbox'>
                <a
                  href={COMPANY_URL.KYB_PRIVACY_POLICY}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary-500 underline hover:no-underline'
                >
                  プライバシーポリシー
                </a>
                に同意する
              </label>
            </div>
            <ErrorMessage>{firstError(errors?.privacy_policy_agreed)}</ErrorMessage>
          </div>

          <button
            type='button'
            className='mx-auto mt-4 w-full max-w-[240px] rounded bg-primary-500 py-3 text-white disabled:cursor-not-allowed disabled:bg-gray-200'
            onClick={handleOpenConfirmModal}
            disabled={!agreedToPrivacyPolicy}
          >
            送信
          </button>
        </form>
      </div>

      {isConfirmModalOpen && (
        <ContactConfirmModal
          onClose={handleCloseConfirmModal}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </main>
  );
}
